// Client-side Siteline aggregator.
// The dedicated `/api/siteline` feed API is still being built (clowalky
// siteline-backend plan). Until it lands, we build the real feed by reading the
// app's existing public domain endpoints and merging them chronologically.
// When the backend feed ships, swap `fetchSitelineEntries` for a single call.
import { apiClient } from "../../../api/client";
import { SERVER_URL } from "../../../config/env";
import { getAccessToken } from "../../../utils/authTokens";
import { store } from "../../../store/store";
import type { CoffeeRateInterface } from "../../../components/coffee_card_web/CoffeeCardWeb/types";
import type { RecentBadgeItem } from "../../badges/types";
import { tierRank } from "../../badges/collection/badgeFormat";
import type { SitelineEntry } from "../types";

const ms = (v: unknown): number => {
  if (!v) return 0;
  const t = new Date(v as string).getTime();
  return Number.isFinite(t) ? t : 0;
};
const difficultyLabel = (d: unknown): string => {
  if (typeof d === "string") return d;
  if (typeof d === "number") return d <= 1.5 ? "easy" : d < 3.5 ? "medium" : "hard";
  return "";
};

// ---------------- per-domain fetchers (each returns [] on failure) ----------------

async function fetchAotd(): Promise<SitelineEntry[]> {
  // every AOTD ever (the public archive), newest sorting handled by the merge.
  const { data } = await apiClient.get("/api/art-of-the-day/all/info");
  const list: any[] = Array.isArray(data) ? data : [];
  return list
    .filter((a) => a && a.filename)
    .map((a) => ({
      id: "aotd:" + a.filename,
      kind: "aotd" as const,
      // clique-initialized days show the clique as the actor, keeping the emoji fallback.
      actor: { username: a.origin?.name || a.createdBy || "n450s", emoji: a.userEmoji || "🎨" },
      occurredAt: ms(a.date),
      payload: {
        // shape matches the gallery's ArtInfo so we can reuse its <Art> tile
        // (which loads the authed image blob — a plain <img src> can't).
        artInfo: {
          title: a.title,
          createdBy: a.createdBy,
          authorQuote: a.authorQuote,
          quoteHelp: Number(a.quoteHelp) || 0,
          filename: a.filename,
          date: new Date(a.date),
          views: Number(a.views) || 0,
          emojis: a.emojis || [],
          waybackMachineUrl: a.waybackMachineUrl,
          origin: a.origin,
        },
      },
    }))
    .filter((e) => e.occurredAt > 0);
}

async function fetchCoffee(): Promise<SitelineEntry[]> {
  const { data } = await apiClient.get("/api/coffee/v2/ratings?sortBy=createdAt&sortOrder=desc");
  const list: CoffeeRateInterface[] = Array.isArray(data) ? data : data?.ratings || [];
  return list.map((c) => ({
    id: "coffee:" + c._id,
    kind: "coffee" as const,
    actor: { username: c.user?.username || "someone", emoji: c.user?.userEmoji || "☕" },
    occurredAt: ms(c.date) || ms(c.updatedAt),
    coffeeData: c,
  }));
}

async function fetchRecipes(): Promise<SitelineEntry[]> {
  const { data } = await apiClient.get("/api/recipes");
  const list: any[] = Array.isArray(data) ? data : data?.recipes || [];
  return list.map((r) => {
    const id = r._id || r.id;
    // stored image entries can be full URLs (baked with a different host/port);
    // take the filename and rebuild against the running backend.
    const raw = Array.isArray(r.images) && r.images.length ? String(r.images[0]) : null;
    const file = raw ? raw.split("/").pop() : null;
    return {
      id: "recipe:" + id,
      kind: "recipe" as const,
      actor: { username: r.chef?.username || r.createdBy || "a chef", emoji: r.chef?.userEmoji || "🍳" },
      occurredAt: ms(r.dateAdded) || ms(r.date),
      payload: {
        title: r.title,
        difficulty: difficultyLabel(r.difficulty),
        time: r.time,
        description: r.description,
        imageUrl: file ? `${SERVER_URL}/api/recipes/images/${id}/${file}` : null,
      },
    };
  }).filter((e) => e.occurredAt > 0).slice(0, 10);
}

async function fetchGallery(): Promise<SitelineEntry[]> {
  const { data } = await apiClient.get("/api/artists-gallery");
  const list: any[] = Array.isArray(data) ? data : data?.artworks || [];
  return list.slice(0, 10).map((a) => ({
    id: "gallery:" + a._id,
    kind: "gallery" as const,
    actor: { username: a.artist?.username || "an artist", emoji: a.artist?.userEmoji || "🖼️" },
    occurredAt: ms(a.dateAdded),
    payload: {
      title: a.title,
      description: a.description,
      artworkId: a._id,
      standalonePageEnabled: !!a.standalonePageEnabled,
      artistUsername: a.artist?.username,
      imageUrl: a.imageFilename
        ? `${SERVER_URL}/api/artists-gallery/images/${encodeURIComponent(a.imageFilename)}`
        : null,
    },
  }));
}

async function fetchBlog(): Promise<SitelineEntry[]> {
  const { data } = await apiClient.get("/api/agent/blog?limit=15");
  const list: any[] = data?.articles || [];
  return list.map((a) => ({
    id: "blog:" + a._id,
    kind: "blog" as const,
    actor: { username: "sitebot", emoji: "🧠", system: true },
    occurredAt: ms(a.publishedAt),
    payload: { title: a.title, excerpt: a.subtitle || "", slug: a.slug },
  }));
}

async function fetchCards(): Promise<SitelineEntry[]> {
  const { data } = await apiClient.get("/api/cards/drop/activity-feed?limit=20");
  const list: any[] = data?.entries || [];
  return list
    .filter((e) => e.outcome === "card" && e.cardName)
    .slice(0, 12)
    .map((e) => ({
      id: "card:" + e.username + ":" + e.claimedAt,
      kind: "card" as const,
      actor: { username: e.username || "someone", emoji: e.userEmoji || "🃏" },
      occurredAt: ms(e.claimedAt),
      payload: {
        cardEmoji: e.cardEmoji || "🎴",
        cardName: e.cardName,
        rarity: e.rarity || undefined,
        pulled: true,
      },
    }));
}

async function fetchComments(): Promise<SitelineEntry[]> {
  // Recent comments across the last few AOTD drops. Each row carries its parent
  // AOTD's filename so the card can deep-scroll to that day's AOTD tile in the
  // feed (id `sl-aotd-<filename>`). Anonymous comments are already redacted
  // server-side (username/userEmoji null) → rendered as "Anonymous" 🕶️.
  const { data } = await apiClient.get("/api/art-of-the-day/comments/recent?days=4");
  const list: any[] = data?.data?.comments || [];
  return list
    .filter((c) => c && c.aotd && c.aotd.filename)
    .map((c) => ({
      id: "comment:" + c._id,
      kind: "comment" as const,
      actor: {
        username: c.isAnonymous ? "Anonymous" : c.username || "someone",
        emoji: c.isAnonymous ? "🕶️" : c.userEmoji || "💬",
      },
      occurredAt: ms(c.createdAt),
      payload: {
        text: c.text,
        emoji: c.emoji,
        isAnonymous: !!c.isAnonymous,
        isAuthor: !!c.isAuthor,
        aotdFilename: c.aotd.filename,
        aotdTitle: c.aotd.title || null,
        aotdDate: c.aotd.date,
      },
    }))
    .filter((e) => e.occurredAt > 0);
}

async function fetchAnnouncements(): Promise<SitelineEntry[]> {
  const { data } = await apiClient.get("/api/agent/announcements?limit=8");
  const list: any[] = data?.announcements || [];
  return list.map((a) => ({
    id: "announcement:" + a._id,
    kind: "announcement" as const,
    actor: { username: "sitebot", emoji: "🧠", system: true },
    occurredAt: ms(a.createdAt),
    payload: { title: a.title, content: a.content, sections: a.sections || [] },
  }));
}

/** Local calendar day of a timestamp, "2026-09-25". */
function localDayKey(t: number): string {
  const d = new Date(t);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** One feed badge: what the card draws (mini art, name, tier, which copy). */
export interface SitelineBadge {
  code: string;
  name: string;
  category: string;
  tier: string;
  number: number | null;
  rarity: RecentBadgeItem["rarity"];
  count: number;
  repeatIndex: number;
  earnedAt: number;
}

/**
 * Recent badges into feed entries: one entry per member per day, its badges best
 * first ("s4b earned Coffee #250 and 2 more"). Exported for tests.
 */
export function mapRecentBadges(items: RecentBadgeItem[]): SitelineEntry[] {
  const groups = new Map<string, { username: string; day: string; badges: SitelineBadge[] }>();
  for (const it of items) {
    if (!it || !it.username || !it.code) continue;
    const earnedAt = ms(it.earnedAt);
    if (!earnedAt) continue;
    const day = localDayKey(earnedAt);
    const key = JSON.stringify([it.username, day]);
    if (!groups.has(key)) groups.set(key, { username: it.username, day, badges: [] });
    groups.get(key)!.badges.push({
      code: it.code,
      name: it.name || it.code,
      category: it.category,
      tier: it.tier,
      number: it.number ?? null,
      rarity: it.rarity || "common",
      count: Number(it.count) || 1,
      repeatIndex: Number(it.repeatIndex) || 1,
      earnedAt,
    });
  }
  const entries: SitelineEntry[] = [];
  groups.forEach(({ username, day, badges }) => {
    // best first: the tier (the colour is the rarity), then the newest
    const sorted = [...badges].sort((a, b) => tierRank(b.tier) - tierRank(a.tier) || b.earnedAt - a.earnedAt);
    const best = sorted[0];
    const more = sorted.length - 1;
    entries.push({
      id: `badge:${username}:${day}`,
      kind: "badge",
      actor: { username, emoji: "🏅" },
      occurredAt: Math.max(...sorted.map((b) => b.earnedAt)),
      payload: {
        username,
        badges: sorted,
        best,
        more,
        headline: `earned ${best.name}${more > 0 ? ` and ${more} more` : ""}`,
      },
    });
  });
  return entries.sort((a, b) => b.occurredAt - a.occurredAt);
}

/**
 * Resolves once the session restore of a fresh page load has finished, or after
 * maxMs. The feed loads on mount, often before the member's token is back.
 */
function authSettled(maxMs = 8000): Promise<void> {
  const ready = () => store.getState().auth.bootstrapStatus === "ready";
  if (ready()) return Promise.resolve();
  return new Promise((resolve) => {
    let unsubscribe = () => {};
    const finish = () => {
      window.clearTimeout(timer);
      unsubscribe();
      resolve();
    };
    const timer = window.setTimeout(finish, maxMs);
    unsubscribe = store.subscribe(() => {
      if (ready()) finish();
    });
  });
}

async function fetchBadges(): Promise<SitelineEntry[]> {
  // Members only. Without a session the 401 would run the refresh, fail, and
  // send a logged-out reader of the feed to /login.
  await authSettled();
  if (!getAccessToken()) return [];
  const { data } = await apiClient.get("/api/badges/recent?days=7");
  const list: RecentBadgeItem[] = Array.isArray(data?.items) ? data.items : [];
  return mapRecentBadges(list);
}

const SOURCES = [
  fetchAotd, fetchCoffee, fetchRecipes, fetchGallery, fetchBlog, fetchCards, fetchAnnouncements,
  fetchComments, fetchBadges,
];

const recipeSlug = (t: string) => t.toLowerCase().replace(/\s+/g, "-");

/** Resolve the in-app route a feed entry deep-links to (null = no destination). */
export function sitelineLink(e: SitelineEntry): string | null {
  const p = e.payload || {};
  switch (e.kind) {
    case "aotd": return "/art-of-the-day/gallery";
    case "coffee": {
      const shopId = e.coffeeData?.coffeeShopRef?._id;
      return shopId ? `/coffeeshops/${shopId}` : "/coffee";
    }
    case "recipe": return p.title ? `/cookbook/${recipeSlug(p.title)}` : "/cookbook";
    case "card": return "/cards";
    case "badge": {
      const who = p.username || e.actor.username;
      return who ? `/badges/u/${encodeURIComponent(who)}` : "/badges";
    }
    case "gallery":
      return p.standalonePageEnabled && p.artworkId
        ? `/artworks/${p.artworkId}`
        : p.artistUsername ? `/art/${encodeURIComponent(p.artistUsername)}` : "/art";
    case "blog": return p.slug ? `/blog/${p.slug}` : "/blog";
    case "announcement": return null;
    // comment cards don't deep-link — their "go to the art of the day" button
    // scrolls to that day's AOTD tile within the feed (handled in the card).
    case "comment": return null;
    default: return null;
  }
}

/** Fetch every source in parallel, merge, sort newest-first. One failing source never breaks the feed. */
export async function fetchSitelineEntries(): Promise<SitelineEntry[]> {
  const results = await Promise.allSettled(SOURCES.map((fn) => fn()));
  const merged: SitelineEntry[] = [];
  for (const r of results) {
    if (r.status === "fulfilled" && Array.isArray(r.value)) merged.push(...r.value);
  }
  return merged
    .filter((e) => e.occurredAt > 0)
    .sort((a, b) => b.occurredAt - a.occurredAt);
}
